"use client";

import React from "react";
import Link from "next/link";

interface Community {
  title: string;
  description: string;
  highlights: string[];
  url: string;
  icon: string;
}

const communities: Community[] = [
  {
    title: "Long Island City Community",
    description:
      "A rapidly developing waterfront neighborhood with stunning Manhattan skyline views. Home to MoMA PS1, numerous art galleries, and modern high-rise apartments. Known for its thriving arts community and easy commute to Manhattan.",
    highlights: ["Gantry Plaza State Park", "MoMA PS1", "Arts District"],
    url: "/long-island-city-community/",
    icon: "🏙️",
  },
  {
    title: "Sunnyside Community",
    description:
      "A friendly, family-oriented neighborhood with tree-lined streets and the historic Sunnyside Gardens. Known for its strong sense of community, diverse dining scene, and affordable housing with a small-town feel in the big city.",
    highlights: ["Sunnyside Gardens", "Family-Friendly", "Diverse Cuisine"],
    url: "/sunnyside-community/",
    icon: "🌳",
  },
  {
    title: "Ditmars Community",
    description:
      "The heart of Astoria's 'Restaurant Row' with authentic Greek restaurants and a multicultural dining scene. Located near Astoria Park and known for its vibrant commercial district and blend of cultures.",
    highlights: ["Greek Restaurants", "Restaurant Row", "Multicultural"],
    url: "/ditmars-community/",
    icon: "🏛️",
  },
];

const LocalCommunities: React.FC = () => {
  return (
    <>
      <section className="relative w-full bg-gradient-to-br from-gray-50 to-white pt-32 pb-12 sm:pt-36 sm:pb-16 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#ECD05B]/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#AE7D2F]/10 to-transparent rounded-full blur-3xl" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 text-center after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:my-5 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)] mb-12">
            Local Communities We Serve in Queens
          </h1>

          <div className="grid gap-8 md:gap-10">
            {communities.map((community, index) => (
              <Link
                key={community.url}
                href={community.url}
                className="group block relative overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 sm:p-10 border border-gray-100 hover:border-[#ECD05B]/30">
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#ECD05B]/5 to-[#AE7D2F]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl" />

                  <div className="relative z-10 grid md:grid-cols-[auto,1fr] gap-6 items-start">
                    {/* Icon */}
                    <div className="text-5xl md:text-6xl transform group-hover:scale-110 transition-transform duration-300">
                      {community.icon}
                    </div>

                    {/* Content */}
                    <div className="space-y-4">
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 group-hover:text-[#d5ac26] transition-colors duration-300 flex items-center">
                        {community.title}
                        <span className="ml-3 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all duration-300 text-[#d5ac26]">
                          →
                        </span>
                      </h3>

                      <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                        {community.description}
                      </p>

                      {/* Highlights */}
                      <div className="flex flex-wrap gap-2 pt-2">
                        {community.highlights.map((highlight) => (
                          <span
                            key={highlight}
                            className="inline-block px-3 py-1 bg-gray-100 group-hover:bg-[#ECD05B]/20 text-sm font-medium text-gray-700 rounded-full transition-colors duration-300"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Decorative gradient line */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-base sm:text-lg text-gray-700 mb-8">
              Smile4Me is proud to serve these vibrant Queens neighborhoods.
              Click on any community above to learn more about our presence and
              services in that area.
            </p>

            <Link
              href="/contact"
              className="inline-block uppercase font-bold text-lg tracking-wide text-white px-6 py-3 bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B] rounded-lg shadow-lg hover:shadow-inner [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)] hover:scale-105 transition-all duration-300 ease-in-out"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LocalCommunities;